import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtMoney, fmtPct, fmtDate } from "../lib/api";
import { Search, Plus, Pencil, Trash2, Upload, Download, ShieldCheck, Building2, Calendar as CalIcon, CheckCircle2, X, RefreshCw, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import MasterBondDialog from "../components/MasterBondDialog";
import CatalogPicker from "../components/CatalogPicker";
import { toast } from "sonner";

const fmtBig = (n) => {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1e9) return `$${(n/1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n/1e6).toFixed(1)}M`;
  return fmtMoney(n);
};

export default function Catalog() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [bonds, setBonds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [maturityYear, setMaturityYear] = useState("ALL");
  const [underPar, setUnderPar] = useState(false);
  const [bigCap, setBigCap] = useState(false);
  const [maturingSoon, setMaturingSoon] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerBond, setPickerBond] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const fileRef = useRef(null);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/catalog/sync/status");
      setSyncStatus(data);
    } catch (err) {
      void err;
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/catalog");
      setBonds(data);
      loadStatus();
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/catalog/sync");
      if (data.status === "ok") {
        const msg = `Sheet sync: +${data.created} created · ${data.updated} updated${data.skipped ? ` · ${data.skipped} skipped` : ""}`;
        if (data.skipped) toast.warning(msg);
        else toast.success(msg);
      } else {
        toast.error(`Sync failed: ${data.error || "unknown"}`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const maturityYears = useMemo(() => {
    const set = new Set();
    bonds.forEach(b => {
      const y = b.maturity_date?.slice(0, 4);
      if (y) set.add(y);
    });
    return Array.from(set).sort();
  }, [bonds]);

  const filtered = useMemo(() => {
    const now = new Date();
    const oneYearOut = new Date(now.getFullYear(), now.getMonth() + 12, now.getDate());
    return bonds.filter(b => {
      if (filter) {
        const f = filter.toLowerCase();
        const hit = b.ticker?.toLowerCase().includes(f) ||
          b.company_name?.toLowerCase().includes(f) ||
          b.fullname?.toLowerCase().includes(f);
        if (!hit) return false;
      }
      if (maturityYear !== "ALL" && b.maturity_date?.slice(0, 4) !== maturityYear) return false;
      if (underPar && !(b.current_price != null && b.current_price < 25)) return false;
      if (bigCap && !(b.market_cap_usd != null && b.market_cap_usd > 1e9)) return false;
      if (maturingSoon) {
        if (!b.maturity_date) return false;
        const m = new Date(b.maturity_date);
        if (!(m >= now && m <= oneYearOut)) return false;
      }
      return true;
    });
  }, [bonds, filter, maturityYear, underPar, bigCap, maturingSoon]);

  const clearFilters = () => {
    setFilter(""); setMaturityYear("ALL"); setUnderPar(false); setBigCap(false); setMaturingSoon(false);
  };
  const anyFilter = filter || maturityYear !== "ALL" || underPar || bigCap || maturingSoon;

  const onDelete = async (id, ticker) => {
    if (!confirm(`Remove ${ticker} from the master catalog?`)) return;
    try {
      await api.delete(`/catalog/${id}`);
      toast.success(`${ticker} removed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/catalog/import", form, { headers: { "Content-Type": "multipart/form-data" } });
      const errCount = data.errors?.length || 0;
      const errSummary = errCount
        ? ` · ${errCount} error${errCount === 1 ? "" : "s"} (rows: ${data.errors.slice(0, 5).map(e => e.row || "?").join(", ")}${errCount > 5 ? "…" : ""})`
        : "";
      toast.success(`Created ${data.created} · Updated ${data.updated}${errSummary}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { e.target.value = ""; }
  };

  const downloadCsv = async (url, name) => {
    const res = await api.get(url, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = name;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const openAddToPortfolio = (bond) => {
    setPickerBond(bond);
    setPickerOpen(true);
  };

  return (
    <div className="min-h-screen bg-champagne">
      <div className="border-b border-stone px-8 py-5 sticky top-0 bg-champagne z-10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold flex items-center gap-2">
              // SYS::MASTER_CATALOG
              {isAdmin && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-paars/30 text-paars-dark text-[9px]"><ShieldCheck className="w-2.5 h-2.5" /> ADMIN</span>}
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-graphite">BabyBond Catalog</h1>
            <p className="text-xs text-graphite/60 mt-1">{bonds.length} bonds available · pick one to add to your portfolio</p>
          </div>
        </div>
        <div className="flex gap-2">
          {syncStatus?.sheet_url_configured && (
            <button data-testid="sync-sheet-btn" onClick={onSync} disabled={syncing || !isAdmin} className="px-3 py-2 border border-paars text-paars-dark text-xs hover:bg-paars/20 flex items-center gap-1.5 disabled:opacity-50" title={isAdmin ? "Sync from Google Sheet" : "Admin only"}>
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "SYNCING..." : "SYNC SHEET"}
            </button>
          )}
          {isAdmin && (
            <>
              <input ref={fileRef} type="file" accept=".csv" onChange={onImport} className="hidden" data-testid="catalog-import-input" />
              <button data-testid="catalog-template-btn" onClick={() => downloadCsv("/catalog/template/csv", "catalog_template.csv")} className="px-3 py-2 border border-stone text-graphite/80 text-xs hover:bg-stone/50 flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> TEMPLATE
              </button>
              <button data-testid="catalog-import-btn" onClick={() => fileRef.current?.click()} className="px-3 py-2 border border-stone text-graphite/80 text-xs hover:bg-stone/50 flex items-center gap-1.5">
                <Upload className="w-3.5 h-3.5" /> IMPORT
              </button>
              <button data-testid="catalog-export-btn" onClick={() => downloadCsv("/catalog/export/csv", `catalog_${new Date().toISOString().slice(0,10)}.csv`)} disabled={bonds.length===0} className="px-3 py-2 border border-stone text-graphite/80 text-xs hover:bg-stone/50 flex items-center gap-1.5 disabled:opacity-50">
                <Download className="w-3.5 h-3.5" /> EXPORT
              </button>
              <button data-testid="catalog-add-btn" onClick={() => { setEditing(null); setDialogOpen(true); }} className="px-4 py-2 bg-paars text-graphite font-semibold text-xs hover:bg-paars-light flex items-center gap-1.5">
                <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> ADD BOND
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-8 space-y-6">
        {syncStatus?.last_sync && (
          <div data-testid="sync-banner" className={`border ${syncStatus.last_sync.status === "ok" ? "border-stone bg-stone/20" : "border-burgundy/40 bg-burgundy/10"} p-3 flex items-center justify-between text-xs font-mono`}>
            <div className="flex items-center gap-3">
              {syncStatus.last_sync.status === "ok"
                ? <CheckCircle2 className="w-4 h-4 text-groen-dark" />
                : <AlertCircle className="w-4 h-4 text-burgundy" />}
              <div>
                <span className="text-graphite/55 tracking-widest uppercase">Last sync </span>
                <span className="text-graphite">{new Date(syncStatus.last_sync.finished_at || syncStatus.last_sync.started_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}</span>
                {syncStatus.last_sync.status === "ok" && (
                  <span className="text-graphite/55 ml-3">
                    <span className="text-groen-dark">+{syncStatus.last_sync.created}</span> created · {syncStatus.last_sync.updated} updated
                    {syncStatus.last_sync.skipped > 0 && <span className="text-burgundy"> · {syncStatus.last_sync.skipped} skipped</span>}
                  </span>
                )}
                {syncStatus.last_sync.status !== "ok" && <span className="text-burgundy ml-3">{syncStatus.last_sync.error}</span>}
              </div>
            </div>
            <div className="text-graphite/45 tracking-widest uppercase">
              Auto-sync every {syncStatus.interval_hours}h
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-graphite/40" />
              <input
                data-testid="catalog-filter"
                value={filter}
                onChange={(e)=>setFilter(e.target.value)}
                placeholder="SEARCH TICKER OR COMPANY..."
                className="bg-white border border-stone pl-9 pr-3 py-2 text-sm font-mono text-graphite focus:border-paars-dark focus:outline-none w-80 placeholder:text-graphite/30 uppercase tracking-widest"
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[10px] tracking-[0.2em] uppercase text-graphite/55 font-bold">Maturity year</span>
              <select
                data-testid="filter-maturity-year"
                value={maturityYear}
                onChange={(e)=>setMaturityYear(e.target.value)}
                className="bg-white border border-stone px-3 py-2 text-sm font-mono text-graphite focus:border-paars-dark focus:outline-none"
              >
                <option value="ALL">ALL</option>
                {maturityYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            <FilterPill testid="pill-under-par" active={underPar} onClick={()=>setUnderPar(v=>!v)}>Under $25</FilterPill>
            <FilterPill testid="pill-big-cap" active={bigCap} onClick={()=>setBigCap(v=>!v)}>Market Cap &gt; $1B</FilterPill>
            <FilterPill testid="pill-maturing-soon" active={maturingSoon} onClick={()=>setMaturingSoon(v=>!v)}>Maturing within 12mo</FilterPill>

            {anyFilter && (
              <button onClick={clearFilters} className="text-[10px] tracking-widest uppercase text-graphite/55 hover:text-burgundy flex items-center gap-1">
                <X className="w-3 h-3" /> CLEAR
              </button>
            )}

            <div className="ml-auto text-xs font-mono text-graphite/45">
              Showing <span className="text-paars-dark">{filtered.length}</span> / {bonds.length}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-graphite/45 font-mono text-sm animate-pulse">Loading catalog...</div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-stone bg-white p-16 text-center">
            <Building2 className="w-10 h-10 text-graphite/30 mx-auto mb-3" strokeWidth={1.2} />
            <div className="text-sm text-graphite/60 mb-4">
              {bonds.length === 0 ? "Catalog is empty." : "No bonds match your search."}
            </div>
            {isAdmin && bonds.length === 0 && (
              <div className="flex gap-2 justify-center">
                <button onClick={() => { setEditing(null); setDialogOpen(true); }} className="px-4 py-2 bg-paars text-graphite font-semibold text-xs hover:bg-paars-light">+ ADD FIRST BOND</button>
                <button onClick={() => fileRef.current?.click()} className="px-4 py-2 border border-stone text-graphite/80 text-xs hover:bg-stone/50">IMPORT CSV</button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-px bg-stone border border-stone">
            {filtered.map((b) => (
              <BondCard
                key={b.id}
                bond={b}
                isAdmin={isAdmin}
                onAdd={() => openAddToPortfolio(b)}
                onEdit={() => { setEditing(b); setDialogOpen(true); }}
                onDelete={() => onDelete(b.id, b.ticker)}
              />
            ))}
          </div>
        )}
      </div>

      <MasterBondDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} onSaved={load} />
      <CatalogPicker open={pickerOpen} onOpenChange={setPickerOpen} bond={pickerBond} onSaved={() => { setPickerOpen(false); toast.success("Position added to portfolio"); }} />
    </div>
  );
}

function BondCard({ bond, isAdmin, onAdd, onEdit, onDelete }) {
  const nextEx = bond.upcoming_ex_div_detail?.[0];
  const nextPay = bond.upcoming_payment_detail?.[0];
  return (
    <div className="bg-white p-5 flex flex-col gap-3" data-testid={`catalog-card-${bond.ticker}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <div className="text-xl font-mono font-bold text-paars-dark">{bond.ticker}</div>
            <div className="text-[10px] tracking-widest uppercase text-graphite/45 font-mono">{bond.coupon_rate}%</div>
          </div>
          <div className="text-xs text-graphite/70 truncate max-w-[260px]" title={bond.fullname}>{bond.fullname || "—"}</div>
          <div className="text-[11px] text-graphite/50 truncate max-w-[260px]">{bond.company_name || "—"}</div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1 opacity-60 hover:opacity-100 transition-opacity">
            <button onClick={onEdit} title="Edit" className="p-1.5 text-graphite/60 hover:text-graphite hover:bg-stone/50">
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button onClick={onDelete} title="Remove" className="p-1.5 text-graphite/60 hover:text-burgundy hover:bg-stone/50">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-px bg-stone">
        <Cell label="Price" value={bond.current_price ? fmtMoney(bond.current_price) : "—"} sub={bond.price_as_of ? `as of ${new Date(bond.price_as_of).toLocaleString("en-US",{month:"short",day:"2-digit"})}` : "no data"} />
        <Cell label="Yield on Par" value={fmtPct(bond.yield_on_par)} sub={bond.current_yield ? `Curr: ${fmtPct(bond.current_yield)}` : ""} accent="text-paars-dark" />
        <Cell label="YTM" value={bond.ytm ? fmtPct(bond.ytm) : "—"} sub="Approx" accent="text-paars-dark" />
        <Cell label="Per Payment" value={fmtMoney(bond.amount_per_distribution)} sub={`${bond.payment_frequency}×/yr`} accent="text-groen-dark" />
      </div>

      <div className="grid grid-cols-2 gap-3 text-[11px]">
        <Item icon={CalIcon} label="Maturity" value={fmtDate(bond.maturity_date)} />
        <Item icon={CalIcon} label="Call Date" value={bond.call_date ? fmtDate(bond.call_date) : "—"} />
        <DateItem label="Next Ex-Div" date={nextEx} />
        <DateItem label="Next Payment" date={nextPay} />
      </div>

      <div className="border-t border-stone pt-2 grid grid-cols-2 gap-3">
        <Stat label="Outstanding" value={bond.total_outstanding_usd != null ? fmtBig(bond.total_outstanding_usd) : "—"} />
        <Stat label="Market Cap" value={bond.market_cap_usd != null ? fmtBig(bond.market_cap_usd) : "—"} accent={bond.market_cap_usd != null && bond.market_cap_usd > 1e9 ? "text-groen-dark" : ""} />
      </div>

      <button
        data-testid={`add-to-portfolio-${bond.ticker}`}
        onClick={onAdd}
        className="mt-1 w-full py-2 bg-paars text-graphite font-semibold text-xs hover:bg-paars-light flex items-center justify-center gap-2"
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> ADD TO PORTFOLIO
      </button>
    </div>
  );
}

function FilterPill({ children, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition-colors ${
        active ? "bg-paars text-graphite border-paars" : "bg-white text-graphite/70 border-stone hover:border-paars-dark/50"
      }`}
    >
      {children}
    </button>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[9px] tracking-[0.2em] uppercase text-graphite/45 font-bold">{label}</div>
      <div className={`text-sm font-mono mt-0.5 ${accent || "text-graphite/80"}`}>{value}</div>
    </div>
  );
}

function DateItem({ label, date }) {
  return (
    <div className="flex items-center gap-2">
      {date?.confirmed ? (
        <CheckCircle2 className="w-3 h-3 text-groen-dark" strokeWidth={2} />
      ) : (
        <CalIcon className="w-3 h-3 text-graphite/40" strokeWidth={1.5} />
      )}
      <div>
        <div className="text-[9px] tracking-widest uppercase text-graphite/45 font-bold flex items-center gap-1">
          {label}
          {date?.confirmed && <span className="text-groen-dark text-[8px]">CONFIRMED</span>}
        </div>
        <div className="font-mono text-graphite/75 text-[11px]">{date ? fmtDate(date.date) : "—"}</div>
      </div>
    </div>
  );
}

function Cell({ label, value, sub, accent }) {
  return (
    <div className="bg-white p-2.5">
      <div className="text-[9px] tracking-[0.2em] uppercase font-bold text-graphite/45">{label}</div>
      <div className={`text-base font-mono mt-0.5 ${accent || "text-graphite"}`}>{value}</div>
      {sub && <div className="text-[9px] font-mono text-graphite/40 mt-0.5">{sub}</div>}
    </div>
  );
}

function Item({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-3 h-3 text-graphite/40" strokeWidth={1.5} />
      <div>
        <div className="text-[9px] tracking-widest uppercase text-graphite/45 font-bold">{label}</div>
        <div className="font-mono text-graphite/75 text-[11px]">{value}</div>
      </div>
    </div>
  );
}
